import { FormProvider } from '@/context/FormProvider';

import {
  type CreateToolFormValues,
  createToolSchema,
  type UpdateToolFormValues,
  updateToolSchema,
} from '@/schemas/tool.schema';

import type { MCPServerHTTPConfig, Tool } from '@/types/tool.type';
import { TOOL_STATUS, TOOL_TYPE } from '@/types/tool.type';

type CreateProps = {
  children: React.ReactNode;
  onSubmit: (values: CreateToolFormValues) => void;
};

export const CreateToolFormProvider = ({ children, onSubmit }: CreateProps) => {
  const initialValues: CreateToolFormValues = {
    name: '',
    description: '',
    type: TOOL_TYPE.MCP,
    status: TOOL_STATUS.Active,
    enabled: true,
    url: '',
    timeout: 5000,
    client_session_timeout_seconds: 5000,
    headers: [],
    query_params: [],
  };

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={createToolSchema}
      onSubmit={onSubmit}
    >
      {children}
    </FormProvider>
  );
};

const toKeyValueList = (record?: Record<string, string> | null) =>
  record
    ? Object.entries(record).map(([key, value]) => ({ uid: crypto.randomUUID(), key, value }))
    : [];

const buildInitialValues = (tool: Tool): UpdateToolFormValues => {
  const isMCP = tool.type === 'MCP';
  const mcpConfig = isMCP ? (tool.config as MCPServerHTTPConfig | null) : null;

  return {
    name: tool.name,
    description: tool.description,
    status: tool.status,
    enabled: tool.enabled,
    ...(isMCP && {
      url: mcpConfig?.url ?? '',
      timeout: mcpConfig?.timeout ?? 5000,
      client_session_timeout_seconds: mcpConfig?.client_session_timeout_seconds ?? 5000,
      headers: toKeyValueList(mcpConfig?.headers),
      query_params: toKeyValueList(mcpConfig?.query_params),
    }),
  };
};

type UpdateProps = {
  children: React.ReactNode;
  tool: Tool;
  onSubmit: (values: UpdateToolFormValues) => void;
};

export const UpdateToolFormProvider = ({ children, tool, onSubmit }: UpdateProps) => {
  return (
    <FormProvider
      initialValues={buildInitialValues(tool)}
      validationSchema={updateToolSchema}
      onSubmit={onSubmit}
    >
      {children}
    </FormProvider>
  );
};
